/**
 * @file     AccountPhoneVerify.tsx
 * @purpose  Confirm a signed-in person's phone with a one-time code: sends on
 *           mount, auto-submits at six digits, resends after a cooldown, and
 *           offers a skip so a delivery problem never locks anyone out. Used by
 *           the VOW gate, account settings and the partner profile.
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useAppTheme } from '@/core/theme';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { formatPhoneInput } from '@/utils/formatPhone';

const RESEND_COOLDOWN_S = 30;

export interface AccountPhoneVerifyProps {
  /** E.164 number to confirm. */
  phone: string;
  onVerified: (phone: string) => void;
  /** Shown as "Skip for now"; omit to hide the skip. */
  onSkip?: () => void;
  /** Headline override; defaults to the generic "Enter the code" line. */
  headline?: string;
}

type SendState = 'sending' | 'sent' | 'failed';

export function AccountPhoneVerify({ phone, onVerified, onSkip, headline }: AccountPhoneVerifyProps) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [sendState, setSendState] = useState<SendState>('sending');
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [verified, setVerified] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const checked = useRef(new Set<string>());
  const started = useRef(false);

  const errorFor = useCallback(
    (error: string | undefined) => {
      if (error === 'rate_limited') {
        return t('phoneVerify.rateLimited', { defaultValue: 'Too many attempts. Wait a moment, then try again.' });
      }
      if (error === 'invalid_phone') {
        return t('phoneVerify.invalidPhone', { defaultValue: 'That number does not look right. Check it in your profile.' });
      }
      return t('phoneVerify.sendFailed', { defaultValue: 'The code could not be sent. Try again in a moment.' });
    },
    [t],
  );

  const send = useCallback(async () => {
    setSendState('sending');
    setErrorText(null);
    try {
      const res = await secureFetch('/api/phone/verify/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const json = (await res.json()) as { ok?: boolean; error?: string };
      if (res.ok && json.ok) {
        setSendState('sent');
        setCooldown(RESEND_COOLDOWN_S);
        return;
      }
      setSendState('failed');
      setErrorText(errorFor(json.error));
    } catch {
      setSendState('failed');
      setErrorText(errorFor(undefined));
    }
  }, [phone, errorFor]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void send();
  }, [send]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const submit = useCallback(
    async (value: string) => {
      if (checking || verified || checked.current.has(value)) return;
      checked.current.add(value);
      setChecking(true);
      setErrorText(null);
      try {
        const res = await secureFetch('/api/phone/verify/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, code: value }),
        });
        const json = (await res.json()) as { ok?: boolean; error?: string };
        if (res.ok && json.ok) {
          setVerified(true);
          window.setTimeout(() => onVerified(phone), 700);
          return;
        }
        setErrorText(
          json.error === 'invalid_code'
            ? t('phoneVerify.wrongCode', { defaultValue: 'That code did not match. Check the text and try again.' })
            : json.error === 'rate_limited'
              ? errorFor('rate_limited')
              : t('phoneVerify.checkFailed', { defaultValue: 'The code could not be checked. Try again in a moment.' }),
        );
      } catch {
        setErrorText(t('phoneVerify.checkFailed', { defaultValue: 'The code could not be checked. Try again in a moment.' }));
      } finally {
        setChecking(false);
      }
    },
    [checking, verified, phone, onVerified, errorFor, t],
  );

  const onCodeChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) void submit(digits);
  };

  const display = formatPhoneInput(phone.replace(/^\+1/, ''));
  const textColor = theme.colors.textSecondary;

  if (verified) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} role="status">
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.colors.textPrimary }}>
          {t('phoneVerify.confirmed', { defaultValue: 'Number confirmed' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${theme.layout.form.groupGap}px` }}>
      <Typography sx={{ fontSize: 18, lineHeight: '26px', fontWeight: 600, color: theme.colors.textPrimary }}>
        {headline ?? t('phoneVerify.headline', { defaultValue: 'Enter the code sent to your phone' })}
      </Typography>
      <Typography sx={{ color: textColor, lineHeight: '22px' }}>
        {sendState === 'sending'
          ? t('phoneVerify.sending', { defaultValue: 'Sending a code to {{phone}}…', phone: display })
          : t('phoneVerify.body', { defaultValue: 'A 6-digit code was sent to {{phone}}.', phone: display })}
      </Typography>

      <TextField
        type="text"
        name="one-time-code"
        autoComplete="one-time-code"
        inputMode="numeric"
        autoFocus
        disabled={sendState === 'sending'}
        label={t('phoneVerify.codeLabel', { defaultValue: '6-digit code' })}
        placeholder="123456"
        value={code}
        error={Boolean(errorText)}
        helperText={errorText}
        onChange={(e) => onCodeChange(e.target.value)}
        slotProps={{
          htmlInput: { maxLength: 6, 'aria-label': t('phoneVerify.codeLabel', { defaultValue: '6-digit code' }) },
          input: { endAdornment: checking ? <CircularProgress size={18} /> : undefined },
        }}
        sx={{
          maxWidth: 240,
          '& .MuiOutlinedInput-root': { borderRadius: `${theme.radius.md}px` },
          '& input': { letterSpacing: '0.35em', fontSize: 20, fontWeight: 500 },
        }}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="text"
          size="small"
          disabled={sendState === 'sending' || cooldown > 0}
          onClick={() => void send()}
          sx={{ textTransform: 'none', p: 0, color: textColor }}
        >
          {cooldown > 0 && sendState === 'sent'
            ? t('phoneVerify.resendIn', { defaultValue: 'Resend code in {{seconds}}s', seconds: cooldown })
            : t('phoneVerify.resend', { defaultValue: 'Resend code' })}
        </Button>
        {onSkip ? (
          <Button variant="text" size="small" onClick={onSkip} sx={{ textTransform: 'none', p: 0, color: textColor }}>
            {t('phoneVerify.skip', { defaultValue: 'Skip for now' })}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
